import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { ConnectionBadge } from '@/components/ConnectionBadge';

interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  /** Rota do botão voltar. Omitir em telas de primeiro nível. */
  backTo?: string;
  actions?: ReactNode;
  showConnection?: boolean;
}

/** Cabeçalho de página (F8-T02). */
export function PageHeader({
  title,
  subtitle,
  backTo,
  actions,
  showConnection = false,
}: PageHeaderProps): React.ReactElement {
  return (
    <header className="flex items-start gap-2 py-4">
      {backTo && (
        <Link
          to={backTo}
          aria-label="Voltar"
          className="-ml-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-slate-300 active:bg-slate-800"
        >
          <ChevronLeft size={24} aria-hidden />
        </Link>
      )}

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-xl font-semibold">{title}</h1>
        {subtitle && <div className="mt-0.5 text-sm text-slate-400">{subtitle}</div>}
      </div>

      <div className="flex shrink-0 items-center gap-2 pt-1">
        {showConnection && <ConnectionBadge />}
        {actions}
      </div>
    </header>
  );
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}

/** Estado vazio (F8-T12). */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: EmptyStateProps): React.ReactElement {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      {icon && <div className="text-slate-600">{icon}</div>}
      <p className="font-medium text-slate-200">{title}</p>
      {description && <p className="max-w-xs text-sm text-slate-400">{description}</p>}
      {action}
    </div>
  );
}

interface ErrorStateProps {
  title?: string;
  description?: ReactNode;
  onRetry?: () => void;
}

/** Estado de erro (F8-T13). */
export function ErrorState({
  title = 'Não foi possível carregar',
  description,
  onRetry,
}: ErrorStateProps): React.ReactElement {
  return (
    <div role="alert" className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <p className="font-medium text-slate-200">{title}</p>
      {description && <p className="max-w-xs text-sm text-slate-400">{description}</p>}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="min-h-11 rounded-lg bg-slate-800 px-5 text-sm font-medium active:bg-slate-700"
        >
          Tentar novamente
        </button>
      )}
    </div>
  );
}

/** Esqueleto de carregamento (F8-T11). */
export function Skeleton({ className = '' }: { className?: string }): React.ReactElement {
  return <div className={`animate-pulse rounded-lg bg-slate-800 ${className}`} aria-hidden />;
}

export function SkeletonList({ rows = 6 }: { rows?: number }): React.ReactElement {
  return (
    <div className="flex flex-col gap-2 py-2" role="status" aria-label="Carregando">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  );
}
